import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2 } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { EnvelopeAccountActivityDocument } from './graphql/envelopeAccountActivity.generated';

import { Row } from '../../../activity/components';
import { ActivityLinkFooter } from '../../../Shared/components-v2/activity/overview-cards/ActivityLinkFooter';
import { NoActivityRow } from '../../../Shared/components-v2/activity/overview-cards/NoActivityRow';

export const ActivityCardView: React.FC = () => {
  const id = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(EnvelopeAccountActivityDocument, { variables: { id }, throwOnError: true });
  const { account } = data || {};
  const activity = account && 'activity' in account && account.activity;
  const items = useFallbackKeys(activity ? activity.items : []);

  const legacySubAccountIds = (() => {
    if (!account) return undefined;

    switch (account.__typename) {
      case 'CashAccount':
      case 'Five29Account':
      case 'DirectTradingAccount':
        return [account.legacySubAccountId];
      case 'ManagedAccount':
        return account.subAccounts.map(a => a.legacySubAccountId);
      case 'CheckingAccount':
      case 'ExternalAccountEnvelopeAssociation':
        return undefined;
      default: {
        const exhaustiveCheck: never = account;
        return exhaustiveCheck;
      }
    }
  })();

  if (!data) return <CardLoader />;
  if (!activity) return null;

  const hasActivity = !!items.length;

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">Recent activity</H2>
      </Card.Header>
      <Card.Content>
        {hasActivity ? items.map(({ key, item }) => <Row key={key} item={item} />) : <NoActivityRow />}
      </Card.Content>
      {hasActivity && <ActivityLinkFooter accountIds={legacySubAccountIds?.map(e => e.toString())} />}
    </Card>
  );
};

export const ActivityCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <ActivityCardView />
  </ErrorBoundary>
);
